import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Card, Descriptions, Tag, Progress, Empty, Spin, Button, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import subscriptionService from '../../services/subscriptionService';
import SubscriptionStatusTag from '../../../shared/components/SubscriptionStatusTag';
import { adminPath } from '../../../shared/config/paths';

const CYCLE = { onetime: '一次性', monthly: '月付', quarterly: '季付', semi_annually: '半年付', annually: '年付' };
const FEATURE = { traffic_bytes: '流量', bandwidth_bytes: '流量', requests: '请求数' };
const RESET = { daily: '每日', weekly: '每周', monthly: '每月', quarterly: '每季度', yearly: '每年' };

const fmt = (v, key) => {
  const n = Number(v) || 0;
  if (key === 'traffic_bytes' || key === 'bandwidth_bytes') {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let i = 0;
    let x = n;
    while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
    return `${i ? x.toFixed(2) : x} ${units[i]}`;
  }
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return String(n);
};

const day = (v) => (v ? String(v).slice(0, 10) : '-');

/** 只读:域名所属订阅(产品/状态/周期/用量),改套餐去订阅管理页。 */
export default function Plan() {
  const { domainData } = useOutletContext();
  const subId = domainData?.subscription?.id;
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!subId) { setSub(null); return; }
    let alive = true;
    setLoading(true);
    subscriptionService.getSubscription(subId)
      .then((r) => { if (alive) setSub(r?.success ? r.data : null); })
      .catch(() => { if (alive) setSub(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [subId]);

  if (!subId) return <Empty description="未绑定套餐" />;
  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

  const s = sub || domainData.subscription;
  const usage = sub?.usage || [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="套餐"
        extra={(
          <Link to={adminPath(`/billing/subscriptions?id=${subId}`)}>
            <Button type="link" icon={<LinkOutlined />}>订阅管理</Button>
          </Link>
        )}
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="订阅ID">#{s.id}</Descriptions.Item>
          <Descriptions.Item label="状态"><SubscriptionStatusTag status={s.status} /></Descriptions.Item>
          <Descriptions.Item label="产品">{s.product_name || `#${s.product_id}`}</Descriptions.Item>
          <Descriptions.Item label="计费周期">{CYCLE[s.billing_cycle] || s.billing_cycle || '-'}</Descriptions.Item>
          <Descriptions.Item label="激活日期">{day(s.activation_date)}</Descriptions.Item>
          <Descriptions.Item label="到期日期">{s.expiry_date ? day(s.expiry_date) : '不过期'}</Descriptions.Item>
          <Descriptions.Item label="自动续费">
            <Tag color={s.auto_renew ? 'success' : 'default'}>{s.auto_renew ? '是' : '否'}</Tag>
          </Descriptions.Item>
          {sub?.addons?.length > 0 && (
            <Descriptions.Item label="附加包">
              <Space wrap>
                {sub.addons.map((a) => (
                  <Tag key={a.subscription_id} color={a.status === 'active' ? 'blue' : 'orange'}>
                    {a.name}{a.quantity > 1 ? ` × ${a.quantity}` : ''}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {usage.length > 0 && (
        <Card title="用量" size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {usage.map((u) => {
              const pct = u.is_unlimited || !(u.limit_value > 0) ? 0 : Math.min(100, (u.used_value / u.limit_value) * 100);
              const over = !u.is_unlimited && u.limit_value > 0 && u.used_value > u.limit_value;
              return (
                <Card
                  key={u.id || u.feature_key}
                  size="small"
                  title={<span>{FEATURE[u.feature_key] || u.feature_key} <Tag style={{ marginLeft: 6 }}>{RESET[u.reset_cycle] || u.reset_cycle || '每月'}重置</Tag></span>}
                  extra={over ? <Tag color="error">已超限</Tag> : null}
                >
                  <div style={{ marginBottom: 8 }}>
                    {fmt(u.used_value, u.feature_key)} / {u.is_unlimited ? '无限制' : fmt(u.limit_value, u.feature_key)}
                  </div>
                  {!u.is_unlimited && <Progress percent={Number(pct.toFixed(1))} status={over ? 'exception' : 'normal'} size="small" />}
                  {u.period_end && <div style={{ color: '#999', fontSize: 12 }}>周期至 {day(u.period_end)}</div>}
                </Card>
              );
            })}
          </div>
        </Card>
      )}
    </Space>
  );
}
